import { Form } from 'antd';
import React, { memo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import BackOfficeDrawer from 'components/drawers/BackOfficeDrawer';
import BackOfficeEnterpriseForm from 'components/forms/BackOfficeEnterpriseForm';
import { useBackOfficeEnterprisesRequestParameters } from 'components/requests/useBackOfficeRequestParameters';
import { INSUFFICIENT_PERMISSIONS_MESSAGE } from 'constants/internal-admin-roles';
import { SEARCH_PARAMS } from 'constants/search-params';
import { addBackOfficeEnterprise, loadBackOfficeEnterprises } from 'store/modules/backOfficeEnterprises/actions';
import clearErrors from 'store/modules/error/actions';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import { EnterpriseStatus, Industry } from 'types/models/enterprise';

const actions = ['ADD_BACK_OFFICE_ENTERPRISE'];

function BackOfficeEnterpriseCreateDrawer(): JSX.Element {
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();
  const visible = searchParams.get(SEARCH_PARAMS.DRAWER.key) === SEARCH_PARAMS.DRAWER.values.ADD_ENTERPRISE;

  const dispatch = useDispatch();

  useEffect(() => {
    if (visible) {
      dispatch(clearErrors(actions));
      form.resetFields();
    }
  }, [visible, form, dispatch]);

  const handleClose = () => {
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      newSearchParams.delete(SEARCH_PARAMS.DRAWER.key);
      return newSearchParams;
    });
  };

  const requestParameters = useBackOfficeEnterprisesRequestParameters();

  const onAddSuccess = () => {
    handleClose();
    dispatch(loadBackOfficeEnterprises(requestParameters));
  };

  const submitFormAndCloseDrawer = () => {
    form
      .validateFields()
      .then((values) => {
        dispatch(addBackOfficeEnterprise(values, onAddSuccess));
      })
      .catch(() => {
        // Ignoring promise rejection
      });
  };

  const hasPermission = useMyUserHasInternalPermission({
    resource: ProtectedResource.ENTERPRISE,
    action: ResourceAction.ADD
  });
  if (!hasPermission) {
    return (
      <BackOfficeDrawer
        visible={visible}
        backOfficeSection="Create Enterprise"
        actions={[]}
        noFooter
        handleClose={handleClose}
      >
        {INSUFFICIENT_PERMISSIONS_MESSAGE}
      </BackOfficeDrawer>
    );
  }
  return (
    <BackOfficeDrawer
      visible={visible}
      backOfficeSection="Create Enterprise"
      actions={actions}
      handleSave={submitFormAndCloseDrawer}
      handleClose={handleClose}
    >
      <BackOfficeEnterpriseForm
        actions={actions}
        form={form}
        initialValues={{
          industry: Industry.AGRICULTURE,
          status: EnterpriseStatus.ACTIVE,
          useBillingAddressForShipping: true
        }}
      />
    </BackOfficeDrawer>
  );
}

export default memo(BackOfficeEnterpriseCreateDrawer);
